/**
 * L'ILE 2 — sept cents metres, onze regions.
 *
 * Ce n'est pas une autre carte: c'est une deuxieme ile posee plus loin sur LE
 * MEME plan x/z que la premiere (voir `shared/sim/carte.ts`). Elle travaille
 * donc en coordonnees ABSOLUES, comme tout le reste du jeu — elle sait ou elle
 * est, personne n'a a traduire pour elle.
 *
 * ELLE A ETE ECRITE POUR ETRE REMPLACEE, ET ELLE VIENT DE L'ETRE. La premiere
 * version disait: « le proprietaire l'a demandee carree, pas trop grande pour
 * l'instant, vide avec juste du sable et quelques arbres [...] elle sera
 * dessinee pour de bon dans une phase ulterieure et grandira beaucoup: tout ce
 * qui est ici doit donc pouvoir etre remplace sans que rien d'autre ne bouge ».
 * C'est exactement ce qui s'est passe: le fichier a change entierement, et
 * l'interface `Ile` n'a pas bouge d'une ligne.
 *
 * COMMENT ONZE REGIONS TIENNENT DANS UNE ILE, sans onze fonctions de terrain:
 *
 *  - Un VORONOI DOUX sur un plan deforme par du bruit
 *    (`poidsRegions`) donne les poids. Le plus proche gagne, et sur les treize
 *    metres de part et d'autre de la frontiere les deux se fondent — la meme
 *    largeur de transition que les biomes de l'ile 1, pour que les melanges de
 *    couleur se lisent pareil. Une seule region pese 1 partout ailleurs, donc
 *    le relief ne se calcule qu'une fois par point.
 *  - Le plan est deforme par un CHAMP TABULE (`construireWarp`, pas de 8 m).
 *    Sans lui, les frontieres sont des droites, et une ile decoupee a la regle
 *    se voit tout de suite. C'est la version a deux dimensions de la lecon des
 *    tables de `relief.ts`: le motif le plus fin de ce champ fait une
 *    soixantaine de metres, l'echantillonner au metre serait payer soixante
 *    fois trop cher pour tordre un trait.
 *  - Le relief de chaque region est une RECETTE (`hauteurRegion` + `ReliefDef`),
 *    pas une fonction. Onze fonctions ecrites a la main seraient onze endroits
 *    ou se tromper, et une gorge n'est rien d'autre qu'une crete d'amplitude
 *    NEGATIVE.
 *
 * DEUX CHOSES NE SONT TOUJOURS PAS NEGOCIABLES:
 *
 *  - **Le rivage se remonte partout.** Une cote en falaise est un piege
 *    silencieux: le joueur sait nager, il plonge donc n'importe ou, et si la
 *    pente au niveau de la mer depasse MAX_WALK_SLOPE il vient buter contre son
 *    ile sans jamais y remonter. Le profil de plongee part et arrive a pente
 *    NULLE (c'est ce que fait un `smoothstep`), donc le trait d'eau est le point
 *    le plus PLAT du talus, pas le plus raide.
 *  - **L'arrivee est plate pour de vrai.** Une piste a sa propre tolerance de
 *    denivele, et un batiment qu'on ne peut pas poser est un batiment qui
 *    n'existe pas. Le disque de l'aerodrome est aplani en dernier, apres tout le
 *    reste, exactement comme l'ile 1 aplanit son point d'apparition.
 */
import { CHUNK_SIZE, ILE2_COTE, ILE2_X0, ILE2_Z0, SEA_LEVEL, TERRAIN_CELL } from '../constants.ts'
import {
  NB_REGIONS,
  ORDRE_REGIONS,
  RAYON_FONDU_AERO,
  RAYON_PLAT_AERO,
  REGIONS,
  SITE_AERODROME,
  pointSemence,
} from '../data/ile2-regions.ts'
import type { LacDef, RegionDef, RegionId, ReliefDef } from '../types/ile2.ts'
import type { BiomeId, SolId, TerrainSample, Vec2 } from '../types/world.ts'
import { Alea, clamp, fbm, fbmCrete, hache2, lerp, melangerCouleur, smoothstep } from './alea.ts'
import { construireTable, lireTable, minAdouci } from './relief.ts'
import type { Chute } from './terrain.ts'
import type { Ile } from './carte.ts'

/** Cote de la grille de hauteur de l'ile. */
const N = ILE2_COTE / TERRAIN_CELL + 1

/** Profondeur atteinte au bord exact du carre, en metres. */
const PROFONDEUR_BORD = 4.2

/**
 * Distance horizontale sur laquelle le fond descend de la greve au large.
 *
 * C'est le nombre qui decide si l'on peut ressortir de l'eau. Le talus suit un
 * `smoothstep`, dont la pente maximale vaut 1,5 fois la pente moyenne: pour le
 * profil le plus raide de la table (la plage, qui tombe de 1 m a -16 m), cela
 * fait 17 x 1,5 / 46 = 0,55, soit les deux tiers de `MAX_WALK_SLOPE`. Et cette
 * pente maximale est atteinte a mi-talus, sous l'eau — au trait d'eau lui-meme,
 * la un `smoothstep` est PLAT.
 */
const PLONGEE = 46

/** Largeur de l'adoucissement des coins du carre, en metres. */
const ADOUCI_COTE = 42

/**
 * Recul moyen du trait de cote a l'interieur du carre, et amplitude des criques.
 *
 * LE RECUL N'EST PAS DECORATIF. Il doit etre assez grand pour que le talus de
 * plongee ait FINI sa descente avant d'atteindre le bord du carre, la ou
 * `noyerBordure` prend le relais. Avec un recul minimal de 11 m, la mesure
 * donnait 1,136 de pente au trait d'eau — la bordure tirait vers le fond un
 * terrain encore emerge, sur quatorze metres. A 40 m de recul (donc 21 m au
 * plus serre des criques), le talus est deja sous la mer quand la bordure
 * commence, et elle n'a plus rien a corriger.
 */
const AMPLITUDE_CRIQUE = 26
const RECUL_COTE = 40

/**
 * Amplitudes du champ de deformation, en metres.
 *
 * ELLES SONT BORNEES PAR UN REPLI, et c'est le piege le plus couteux de ce
 * fichier. Un plan deforme dont le GRADIENT depasse 1 se replie sur lui-meme:
 * deux points voisins peuvent alors atterrir tres loin l'un de l'autre dans le
 * plan deforme, donc changer de region d'un metre a l'autre, et revenir au
 * suivant. Le terrain, lui, ne clignote pas — il fait une MARCHE, parce que
 * deux regions voisines n'ont pas le meme profil de rivage.
 *
 * Mesure, a 130 et 34 m d'amplitude: gradient maximal 1,47, region qui alterne
 * tous les deux metres, et une falaise de 2,2 m entre deux metres au bord de
 * l'eau — 1,14 de pente la ou le plafond est 0,85. A 50 et 12, le gradient
 * maximal tombe a 0,55 et il reste vingt-cinq metres de decalage, largement de
 * quoi qu'aucune frontiere ne soit une droite.
 */
const AMPLI_WARP_LARGE = 50
const AMPLI_WARP_FIN = 12

/** Pas de la grille du champ de deformation, en metres. */
const PAS_WARP = 8
const N_WARP = ILE2_COTE / PAS_WARP + 1

/**
 * Largeur de berge accordee par metre de descente vers un plan d'eau.
 *
 * 3,4 m par metre tient la rive a 0,44 de pente au pire, soit la moitie du
 * plafond de marche: on entre dans l'eau et l'on en ressort, quel que soit le
 * relief autour.
 */
const PENTE_BERGE = 3.4

/** Largeur du fondu entre deux regions, en metres de part et d'autre. */
const FONDU_REGION = 13

/** En deca de ce poids, une region ne contribue pas au relief. */
const POIDS_MIN = 0.004

/*
 * LA CASCADE DE L'ILE 2.
 *
 * Elle ne reutilise PAS `choisirChute` de l'ile 1, et c'est une conclusion, pas
 * une paresse: celle-la cherche le rivage d'un LAC et creuse une vasque SOUS le
 * niveau de la mer. L'ile 2 n'a ni l'un ni l'autre a vingt metres d'altitude,
 * et n'en veut pas — l'eau de ce moteur est ce qui est sous zero, donc une
 * vasque perchee serait un puits creuse dans un plateau, et le balayage du
 * trait d'eau le refuserait a juste titre.
 *
 * `Chute` est une FICHE DE DONNEES, pas un mecanisme: chaque ile fabrique la
 * sienne, et le rendu ne fait que la lire.
 *
 * Ce qu'on cherche ici n'est donc pas une rive, c'est LA MARCHE LA PLUS RAIDE
 * du secteur — ce que le relief en gradins de la region fabrique tout seul.
 */
/** Demi-cote du balayage des sites, et son pas, en metres. */
const CHUTE2_PORTEE_SITE = 110
const CHUTE2_PAS_SITE = 6
/** Nombre de directions de courant essayees en chaque site. */
const CHUTE2_DIRECTIONS = 12
/** Ce que vaut, en metres de denivelee, une chute bien tournee vers l'oeil. */
const CHUTE2_POIDS_CAMERA = 2.5
/** Ce que coute un amont accidente, en metres de denivelee par unite de pente. */
const CHUTE2_POIDS_PLAT = 14
/**
 * Sur quelle distance on mesure la marche, en metres.
 *
 * C'est EXACTEMENT la distance qui separe la crete de la plate-forme d'arrivee
 * (`CHUTE2_RAYON_PIED + 2`): mesurer la denivelee sur une autre longueur que
 * celle qu'on va utiliser, c'est noter les candidats sur une question qu'on ne
 * leur posera pas. Premiere version: mesuree sur 12 m, la chute retenue tombait
 * de 5 m.
 */
const CHUTE2_PAS_MARCHE = 9
/** En deca de cette denivelee, ce n'est pas une chute mais une pente. */
const CHUTE2_MARCHE_MIN = 6
const CHUTE2_DEMI_LIT = 2.4
const CHUTE2_LONG_AMONT = 30
/*
 * De combien le lit a le droit de REMONTER par metre, en amont de la crete.
 *
 * L'ile 1 se contente de 0,1: son lit descend d'un lac par une vallee douce,
 * donc il n'a presque rien a corriger. Ici le pays monte par gradins de deux
 * metres et demi, et un plafond aussi serre force le lit a se creuser une
 * TRANCHEE pour rester dans son epure — mesure: 1,03 de pente en travers du lit
 * sur toute sa longueur, contre 0,13 pour l'ile 1. Un ruisseau de montagne
 * descend des terrasses; on le laisse donc suivre le pays.
 */
const CHUTE2_PENTE_MIN = 0.02
const CHUTE2_PENTE_MAX = 0.38
/** Rayon de la plate-forme d'arrivee. Une PLATE-FORME, pas une fosse. */
const CHUTE2_RAYON_PIED = 7
const CHUTE2_BERGE = 2.6
const CHUTE2_PORTEE = 52

/** Ce qui est dans le dernier ourlet du carre est noye: la carte prend le relais. */
const OURLET = 26

interface Warp {
  dx: Float32Array
  dz: Float32Array
}

/** Un lac, en coordonnees du monde. */
interface Lac {
  x: number
  z: number
  r: number
  fond: number
  graine: number
}

/** Un ruisseau qui descend d'un lac vers un autre. */
interface Ruisseau {
  ax: number
  az: number
  bx: number
  bz: number
  largeur: number
  creux: number
}

export class Ile2 implements Ile {
  readonly seed: number
  readonly x0 = ILE2_X0
  readonly z0 = ILE2_Z0
  readonly cote = ILE2_COTE

  /** La Cascade de l'Etoile: choisie dans le secteur que la table declare. */
  chute: Chute | null = null

  private hauteurs: Float32Array
  private regionsGrille: Uint8Array
  private warp: Warp
  /** Recul du trait de cote sur chacun des quatre bords, tabule au metre. */
  private cOuest: Float64Array
  private cEst: Float64Array
  private cNord: Float64Array
  private cSud: Float64Array
  private lacs: Lac[] = []
  private ruisseaux: Ruisseau[] = []
  private hAerodrome = 0
  private tampon = new Float64Array(NB_REGIONS)
  private semences: Vec2[] = []
  private defs: RegionDef[] = []

  constructor(seed: number) {
    // Une graine derivee: deux iles nees du meme nombre auraient le meme
    // relief, et l'oeil le verrait tout de suite.
    this.seed = (seed ^ 0x1_1e02) | 0
    for (const id of ORDRE_REGIONS) {
      const def = REGIONS[id]
      this.defs.push(def)
      this.semences.push(pointSemence(def))
    }
    this.warp = this.construireWarp()
    this.cOuest = construireTable(ILE2_COTE, this.seed + 811, criqueCote)
    this.cEst = construireTable(ILE2_COTE, this.seed + 812, criqueCote)
    this.cNord = construireTable(ILE2_COTE, this.seed + 813, criqueCote)
    this.cSud = construireTable(ILE2_COTE, this.seed + 814, criqueCote)
    this.construireHydro()

    /*
     * La chute se choisit sur le terrain NU, avant de se tailler elle-meme:
     * `this.chute` est encore null ici, donc `brute` ne la voit pas. C'est la
     * meme precaution que l'ile 1 prend avec `chuteChoisie` — sans elle, le
     * site se chercherait dans un relief qu'il vient de creuser.
     */
    this.chute = this.choisirChute()
    // Le torrent part du PIED de la chute et descend vers le Lac Lilas, qui est
    // sous le niveau de la mer: c'est la seule facon qu'il finisse dans de
    // l'eau pour de vrai.
    this.brancherTorrent()

    // L'altitude de l'aerodrome se lit AVANT de remplir la grille, sur le
    // terrain nu: sinon le replat se calerait sur une hauteur qu'il vient
    // lui-meme de modifier.
    this.hAerodrome = this.brute(SITE_AERODROME.x, SITE_AERODROME.z)

    this.hauteurs = new Float32Array(N * N)
    this.regionsGrille = new Uint8Array(N * N)
    this.remplir()
  }

  // ---------------------------------------------------------------- warp

  /**
   * Le champ de deformation: deux tables au pas de 8 m, lues bilineairement.
   *
   * Le motif le plus fin qu'il porte fait une soixantaine de metres, donc
   * huit metres d'echantillonnage coutent quelques centimetres d'erreur sur un
   * champ dont le seul role est de tordre une frontiere. Au metre, ce serait
   * soixante-quatre fois plus de bruit calcule pour le meme trait.
   */
  private construireWarp(): Warp {
    const dx = new Float32Array(N_WARP * N_WARP)
    const dz = new Float32Array(N_WARP * N_WARP)
    for (let iz = 0; iz < N_WARP; iz++) {
      for (let ix = 0; ix < N_WARP; ix++) {
        const x = this.x0 + ix * PAS_WARP
        const z = this.z0 + iz * PAS_WARP
        const i = iz * N_WARP + ix
        dx[i] =
          (fbm(x * 0.0042, z * 0.0042, this.seed + 21, 3) - 0.5) * AMPLI_WARP_LARGE +
          (fbm(x * 0.017, z * 0.017, this.seed + 23, 2) - 0.5) * AMPLI_WARP_FIN
        dz[i] =
          (fbm(x * 0.0042, z * 0.0042, this.seed + 22, 3) - 0.5) * AMPLI_WARP_LARGE +
          (fbm(x * 0.017, z * 0.017, this.seed + 24, 2) - 0.5) * AMPLI_WARP_FIN
      }
    }
    return { dx, dz }
  }

  private lireWarp(t: Float32Array, x: number, z: number): number {
    const gx = clamp((x - this.x0) / PAS_WARP, 0, N_WARP - 1.001)
    const gz = clamp((z - this.z0) / PAS_WARP, 0, N_WARP - 1.001)
    const ix = Math.floor(gx)
    const iz = Math.floor(gz)
    const fx = gx - ix
    const fz = gz - iz
    const i = iz * N_WARP + ix
    const a = t[i] ?? 0
    const b = t[i + 1] ?? a
    const c = t[i + N_WARP] ?? a
    const d = t[i + N_WARP + 1] ?? c
    return lerp(lerp(a, b, fx), lerp(c, d, fx), fz)
  }

  // ------------------------------------------------------------- regions

  /**
   * Les poids des onze regions en un point, normalises, somme exactement 1.
   *
   * Un noyau exponentiel aurait donne a TOUTES les regions un poids non nul
   * partout: le raccourci `POIDS_MIN` ne se serait jamais declenche, et l'on
   * aurait assemble onze reliefs a chaque metre carre. Ici le `smoothstep`
   * contre la MEILLEURE distance rend exactement 1 a l'interieur d'une cellule,
   * exactement 0 au-dela du fondu, et deux valeurs intermediaires sur une
   * frontiere. C'est la meme forme que `calculerPoids` sur l'ile 1.
   */
  poidsRegions(x: number, z: number, sortie: Float64Array): void {
    const X = x + this.lireWarp(this.warp.dx, x, z)
    const Z = z + this.lireWarp(this.warp.dz, x, z)
    let dMin = Infinity
    for (let i = 0; i < NB_REGIONS; i++) {
      const s = this.semences[i] as Vec2
      const def = this.defs[i] as RegionDef
      // Voronoi MULTIPLICATIF: `force` est la taille relative de la cellule.
      const d = Math.hypot(X - s.x, Z - s.z) / def.force
      sortie[i] = d
      if (d < dMin) dMin = d
    }
    let total = 0
    for (let i = 0; i < NB_REGIONS; i++) {
      const v = smoothstep(FONDU_REGION, -FONDU_REGION, (sortie[i] ?? 0) - dMin)
      sortie[i] = v
      total += v
    }
    const inv = total > 1e-6 ? 1 / total : 0
    for (let i = 0; i < NB_REGIONS; i++) sortie[i] = (sortie[i] ?? 0) * inv
  }

  // ------------------------------------------------------------ hydrographie

  /**
   * Les lacs et les ruisseaux, lus dans la TABLE des regions.
   *
   * Ce ne sont pas des tirages: le Lac Lilas est au coeur du Lac Lilas, l'oasis
   * au coeur des Sables. On ne les secoue que de quelques metres, pour qu'ils
   * n'aient pas l'air poses au compas.
   */
  private construireHydro(): void {
    const alea = new Alea((this.seed ^ 0x1ac5) >>> 0)
    for (let i = 0; i < NB_REGIONS; i++) {
      const def = this.defs[i] as RegionDef
      const lac: LacDef | undefined = def.lac
      if (!lac) continue
      const s = this.semences[i] as Vec2
      this.lacs.push({
        x: s.x + alea.entre(-9, 9),
        z: s.z + alea.entre(-9, 9),
        r: lac.rayon,
        fond: lac.fond,
        graine: alea.entier(0, 0xffff),
      })
    }
  }

  /**
   * LE TORRENT, du pied de la chute jusqu'au Lac Lilas.
   *
   * IL N'EXISTAIT PAS, et c'est un bogue a moi qu'il faut raconter parce que sa
   * forme reviendra. Il partait d'un « lac de tete » de la Cascade; j'ai retire
   * cette vasque perchee (a raison: a vingt et un metres d'altitude, c'est un
   * puits), et le lien entre les deux passait par une RECHERCHE DE PROXIMITE,
   * pas par une reference — `lacDe('cascade_etoile')` s'est mis a rendre null,
   * la liste est restee vide, la boucle ne s'est plus jamais executee, et rien
   * n'a proteste. J'ai continue de le decrire dans le journal du projet.
   *
   * Retirer une donnee peut donc eteindre du code en silence. Et meme vivant,
   * il n'aurait rien montre: un sillon creuse dans un plateau reste de la TERRE
   * SECHE, puisque l'eau de ce moteur est ce qui est sous le niveau de la mer.
   * Il part maintenant du pied de la chute et descend vers un lac qui, lui, est
   * bien sous zero — c'est la seule facon qu'il finisse mouille.
   */
  private brancherTorrent(): void {
    const c = this.chute
    const bas = this.lacDe('lac_lilas')
    if (!c || !bas) return
    this.ruisseaux.push({
      ax: c.vasqueX,
      az: c.vasqueZ,
      bx: bas.x,
      bz: bas.z,
      largeur: 6,
      creux: 2.6,
    })
  }

  /**
   * OU TOMBE LA CASCADE: la marche la plus raide du secteur.
   *
   * L'ile 1 cherche le rivage de son lac; ici il n'y a pas de lac, et il ne
   * doit pas y en avoir. Ce qu'on cherche est la plus grande DENIVELEE sur une
   * douzaine de metres — ce que le relief en gradins de la region fabrique tout
   * seul. Le terme de camera est celui de l'ile 1, et pour la meme raison: la
   * vue est fixe, une chute tournee du mauvais cote se cache derriere son
   * propre plateau.
   */
  private choisirChute(): Chute | null {
    const iCascade = ORDRE_REGIONS.indexOf('cascade_etoile')
    const def = iCascade >= 0 ? this.defs[iCascade] : undefined
    const semence = iCascade >= 0 ? this.semences[iCascade] : undefined
    if (!def?.chute || !semence) return null
    const secteur = def.chute.secteur

    /*
     * ON CHERCHE UNE POSITION ET UNE DIRECTION, ET CE SONT DEUX QUESTIONS.
     *
     * La premiere version balayait un SECTEUR autour de la semence, en copiant
     * l'ile 1. Mesure: la meilleure marche du secteur faisait 2,69 m, alors que
     * la region en offre 12,91 ailleurs — le secteur regardait dans la mauvaise
     * direction. L'ile 1 s'en tire parce qu'elle tire ses rayons DEPUIS un lac,
     * si bien que la direction du rayon EST le sens du courant; ici il n'y a pas
     * de lac, donc l'endroit et le sens sont independants.
     *
     * Le terme de camera ne concerne donc que le SENS DU COURANT: c'est lui qui
     * decide si l'on voit la lame ou si elle se cache derriere son plateau.
     */
    const poids = new Float64Array(NB_REGIONS)
    let meilleur: { x: number; z: number; dx: number; dz: number } | null = null
    let meilleurScore = -Infinity

    for (let x = semence.x - CHUTE2_PORTEE_SITE; x <= semence.x + CHUTE2_PORTEE_SITE; x += CHUTE2_PAS_SITE) {
      for (let z = semence.z - CHUTE2_PORTEE_SITE; z <= semence.z + CHUTE2_PORTEE_SITE; z += CHUTE2_PAS_SITE) {
        if (!this.contient(x, z)) continue
        this.poidsRegions(x, z, poids)
        // La chute appartient a SA region: une lame d'eau qui tomberait dans la
        // jungle voisine ne serait plus la Cascade de l'Etoile.
        if ((poids[iCascade] ?? 0) < 0.6) continue

        for (let a = 0; a < CHUTE2_DIRECTIONS; a++) {
          const ang = (a * Math.PI * 2) / CHUTE2_DIRECTIONS
          const dx = Math.cos(ang)
          const dz = Math.sin(ang)
          const haut = this.basDuLit(x, z, dx, dz)
          const pied = this.basDuLit(x + dx * CHUTE2_PAS_MARCHE, z + dz * CHUTE2_PAS_MARCHE, dx, dz)
          const marche = haut - pied
          if (marche < CHUTE2_MARCHE_MIN) continue
          // On ne creuse rien sous zero: une chute qui tombe au ras de l'eau
          // n'aurait pas de plate-forme ou tomber.
          if (pied < SEA_LEVEL + 2) continue
          const amont = this.basDuLit(x - dx * 10, z - dz * 10, dx, dz)
          /*
           * UNE CHUTE EST UNE TABLETTE QUI S'ARRETE, pas une pente raide de
           * plus. Sans ce terme, le meilleur score allait a un flanc de
           * montagne: la denivelee y est belle, mais le lit se retrouve en
           * travers d'une pente que le joueur ne peut pas remonter (1,03 de
           * pente mesuree, contre 0,13 pour le ruisseau de l'ile 1, qui coule
           * dans une vallee). On note donc aussi le PLAT de l'amont.
           */
          const rugosite = this.rugositeAmont(x, z, dx, dz)
          const score =
            marche +
            0.8 * Math.min(amont - haut, 3) +
            CHUTE2_POIDS_CAMERA * Math.cos(ang - secteur) -
            CHUTE2_POIDS_PLAT * rugosite
          if (score > meilleurScore) {
            meilleurScore = score
            meilleur = { x, z, dx, dz }
          }
        }
      }
    }
    if (!meilleur) return null

    // Le lit en amont, tabule au metre: il remonte, d'une pente bornee, pour
    // que le ruisseau du haut se voie couler vers le vide.
    const lit = new Float64Array(CHUTE2_LONG_AMONT + 1)
    lit[0] = this.basDuLit(meilleur.x, meilleur.z, meilleur.dx, meilleur.dz)
    for (let k = 1; k <= CHUTE2_LONG_AMONT; k++) {
      const x = meilleur.x - meilleur.dx * k
      const z = meilleur.z - meilleur.dz * k
      const bas = this.basDuLit(x, z, meilleur.dx, meilleur.dz)
      const precedent = lit[k - 1] ?? 0
      lit[k] = clamp(bas, precedent + CHUTE2_PENTE_MIN, precedent + CHUTE2_PENTE_MAX)
    }

    const piedX = meilleur.x + meilleur.dx * (CHUTE2_RAYON_PIED + 2)
    const piedZ = meilleur.z + meilleur.dz * (CHUTE2_RAYON_PIED + 2)
    const hPied = this.basDuLit(piedX, piedZ, meilleur.dx, meilleur.dz)
    return {
      x: meilleur.x,
      z: meilleur.z,
      dx: meilleur.dx,
      dz: meilleur.dz,
      hCrete: lit[0] ?? 0,
      demiLit: CHUTE2_DEMI_LIT,
      vasqueX: piedX,
      vasqueZ: piedZ,
      vasqueR: CHUTE2_RAYON_PIED,
      // Une PLATE-FORME, au niveau du sol: le fond et le bassin sont la meme
      // altitude, donc rien n'est creuse et rien ne passe sous la mer.
      vasqueFond: hPied,
      hBassin: hPied,
      longAmont: CHUTE2_LONG_AMONT,
      lit,
    }
  }

  /**
   * A quel point le pays est accidente en amont d'un site, sans unite.
   *
   * On mesure la denivelee EN TRAVERS du courant, a trois hauteurs de remontee:
   * c'est elle qui decide si l'on pourra marcher le long du ruisseau, et c'est
   * exactement ce que la pente le long de l'axe ne dit pas.
   */
  private rugositeAmont(x: number, z: number, dx: number, dz: number): number {
    let pire = 0
    for (const k of [6, 12, 18]) {
      const px = x - dx * k
      const pz = z - dz * k
      const g = this.brute(px - dz * 4, pz + dx * 4)
      const d = this.brute(px + dz * 4, pz - dx * 4)
      const ecart = Math.abs(g - d) / 8
      if (ecart > pire) pire = ecart
    }
    return pire
  }

  /** Point le plus bas d'une section de lit, en travers du courant. */
  private basDuLit(x: number, z: number, dx: number, dz: number): number {
    let bas = Infinity
    for (let k = -2; k <= 2; k++) {
      const n = (k * CHUTE2_DEMI_LIT) / 2
      const h = this.brute(x - dz * n, z + dx * n)
      if (h < bas) bas = h
    }
    return bas
  }

  /**
   * Le lit et la plate-forme, tailles dans le relief deja forme.
   *
   * Comme les lacs, on ne creuse QUE vers le bas — la chute ne peut donc jamais
   * fabriquer une bosse — et JAMAIS sous le niveau de la mer: c'est ce qui
   * garantit qu'elle n'ajoute pas un seul point de trait d'eau, donc pas une
   * seule rive infranchissable.
   */
  private sculpterChute(x: number, z: number, h: number): number {
    const c = this.chute
    if (!c) return h
    const px = x - c.x
    const pz = z - c.z
    if (px * px + pz * pz > CHUTE2_PORTEE * CHUTE2_PORTEE) return h
    const s = px * c.dx + pz * c.dz
    const n = Math.abs(px * c.dz - pz * c.dx)
    let r = h

    // Le lit, en amont de la bascule, avec un leger debord: sans lui les
    // sommets tombes juste apres la crete gardent leur hauteur et la crete se
    // retrouve dentelee.
    if (s <= 1.5 && s > -c.longAmont) {
      const remont = Math.max(0, -s)
      const i = Math.floor(remont)
      const a = c.lit[i] ?? c.hCrete
      const b = c.lit[i + 1] ?? a
      const lit = a + (b - a) * (remont - i)
      const denivele = r - lit
      if (denivele > 0) {
        const large = c.demiLit + clamp(denivele * CHUTE2_BERGE, 2.5, 9)
        if (n < large) {
          const hl = lit + denivele * smoothstep(c.demiLit, large, n)
          if (hl < r) r = Math.max(hl, SEA_LEVEL + 0.5)
        }
      }
    }

    // La plate-forme d'arrivee: plate, a l'altitude du sol, jamais creusee.
    const vx = x - c.vasqueX
    const vz = z - c.vasqueZ
    const dd = Math.hypot(vx, vz)
    if (dd < c.vasqueR + 10) {
      const cible = lerp(c.hBassin, r, smoothstep(c.vasqueR, c.vasqueR + 10, dd))
      if (cible < r) r = Math.max(cible, SEA_LEVEL + 0.5)
    }
    return r
  }

  private lacDe(id: RegionId): Lac | null {
    const i = ORDRE_REGIONS.indexOf(id)
    if (i < 0) return null
    const s = this.semences[i]
    if (!s) return null
    let meilleur: Lac | null = null
    let meilleureD = Infinity
    for (const l of this.lacs) {
      const d = Math.hypot(l.x - s.x, l.z - s.z)
      if (d < meilleureD) {
        meilleureD = d
        meilleur = l
      }
    }
    return meilleureD < 40 ? meilleur : null
  }

  /**
   * Creuse les lacs et les ruisseaux. VERS LE BAS UNIQUEMENT, toujours: un lac
   * qui pourrait remonter le terrain ferait une bosse d'eau, et un ruisseau qui
   * remonte barre son propre estuaire.
   */
  private hydrographie(x: number, z: number, h: number): number {
    let r = h
    for (const l of this.lacs) {
      const chute = r - l.fond
      if (chute <= 0) continue
      // LA BERGE S'ELARGIT AVEC LA DESCENTE, et c'est tout le sujet.
      //
      // Une largeur fixe donne une cuvette dont les parois sont aussi hautes
      // que le pays est haut: un lac de montagne devient un puits. Mesure faite
      // avant de le comprendre: 78 points de rive infranchissables autour du
      // seul lac de la Cascade. La pente maximale d'un `smoothstep` vaut 1,5
      // fois la moyenne, donc une berge de `PENTE_BERGE` fois la descente tient
      // la rive a 1,5 / 3,4, soit 0,44 — la moitie du plafond de marche.
      const bord = Math.max(l.r * 1.25, l.r * 0.35 + PENTE_BERGE * chute)
      const d = Math.hypot(x - l.x, z - l.z)
      if (d > bord) continue
      const t = smoothstep(bord, l.r * 0.35, d)
      const creuse = lerp(r, l.fond, t)
      if (creuse < r) r = creuse
    }
    for (const s of this.ruisseaux) {
      const bord = Math.max(s.largeur * 2.6, s.largeur * 0.4 + PENTE_BERGE * s.creux)
      const d = distanceSegment(x, z, s.ax, s.az, s.bx, s.bz)
      if (d > bord) continue
      const t = smoothstep(bord, s.largeur * 0.4, d)
      const creuse = r - s.creux * t
      if (creuse < r) r = creuse
    }
    return r
  }

  // ---------------------------------------------------------------- relief

  /** Distance signee au trait de cote: positive a terre, negative en mer. */
  private distanceCote(x: number, z: number): number {
    const u = x - this.x0
    const v = z - this.z0
    const dO = u - lireTable(this.cOuest, v)
    const dE = this.cote - u - lireTable(this.cEst, v)
    const dN = v - lireTable(this.cNord, u)
    const dS = this.cote - v - lireTable(this.cSud, u)
    // Les coins se fondent au lieu de se couper: des caps arrondis, pas des
    // angles droits. Exactement ce que fait l'ile 1.
    return minAdouci(minAdouci(dO, dE, ADOUCI_COTE), minAdouci(dN, dS, ADOUCI_COTE), ADOUCI_COTE)
  }

  /**
   * Le champ de hauteur, calcule.
   *
   * L'ordre est celui de l'ile 1, et il n'est pas negociable: on assemble le
   * relief des regions, PUIS on le raccorde a la mer, PUIS on creuse l'eau
   * douce. Creuser avant de raccorder remplirait les lacs de talus.
   */
  private brute(x: number, z: number): number {
    const p = this.tampon
    this.poidsRegions(x, z, p)
    return this.assembler(x, z, p)
  }

  /**
   * Le terrain a partir de poids DEJA calcules.
   *
   * Il est separe de `brute` pour une raison de cout, pas d'elegance: le
   * remplissage de la grille a besoin des memes poids DEUX fois — une fois pour
   * la hauteur, une fois pour savoir quelle region domine. Les recalculer
   * doublait le prix du seul endroit ou ce fichier passe du temps. Les faire
   * traverser dans le tampon partage aurait marche aussi, et aurait tenu tant
   * que personne n'ajoute une lecture du tampon au milieu: on les passe donc en
   * argument, ou personne ne peut se tromper.
   */
  private assembler(x: number, z: number, p: Float64Array): number {
    let relief = 0
    let greve = 0
    let largeur = 0
    let chute = 0
    for (let i = 0; i < NB_REGIONS; i++) {
      const w = p[i] ?? 0
      if (w <= POIDS_MIN) continue
      const def = this.defs[i] as RegionDef
      relief += w * hauteurRegion(def.relief, x, z, this.seed, i)
      greve += w * def.cote.greve
      largeur += w * def.cote.largeur
      chute += w * def.cote.chute
    }

    const d = this.distanceCote(x, z)
    let h: number
    if (d >= 0) {
      // A terre: on remonte de la greve au relief de la region.
      h = lerp(greve, relief, smoothstep(0, Math.max(largeur, 1), d))
    } else {
      // En mer: on descend de la greve au large. Un `smoothstep` part et arrive
      // a pente nulle, donc le trait d'eau est plat — c'est ce qui permet d'en
      // ressortir a la nage.
      h = lerp(greve, -chute, smoothstep(0, PLONGEE, -d))
    }

    h = this.hydrographie(x, z, h)
    h = this.sculpterChute(x, z, h)
    return this.noyerBordure(x, z, h)
  }

  /**
   * Le dernier ourlet du carre est noye.
   *
   * `Carte` prend le relais hors de l'emprise et fait descendre le fond vers le
   * large; si l'ile finissait a une hauteur quelconque sur son bord, il y aurait
   * une MARCHE tout autour, exactement celle que le rendu de l'eau a deja eu a
   * effacer une fois.
   */
  private noyerBordure(x: number, z: number, h: number): number {
    const u = x - this.x0
    const v = z - this.z0
    const d = Math.min(u, v, this.cote - u, this.cote - v)
    if (d >= OURLET) return h
    // On ne fait que CREUSER: si le talus est deja plus bas que la bordure ne
    // le demande, on le laisse tranquille. Remonter le fond ferait une bosse
    // sous l'eau tout autour de l'ile.
    return Math.min(h, lerp(-PROFONDEUR_BORD, h, smoothstep(0, OURLET, d)))
  }

  private remplir(): void {
    const p = this.tampon
    for (let iz = 0; iz < N; iz++) {
      for (let ix = 0; ix < N; ix++) {
        const x = this.x0 + ix * TERRAIN_CELL
        const z = this.z0 + iz * TERRAIN_CELL
        this.poidsRegions(x, z, p)
        let h = this.assembler(x, z, p)

        // L'aplanissement de l'aerodrome vient EN DERNIER, comme le point
        // d'apparition de l'ile 1: c'est la que l'on arrive du monde 1, et une
        // piste ne se pose pas sur une dune.
        const dAero = Math.hypot(x - SITE_AERODROME.x, z - SITE_AERODROME.z)
        if (dAero < RAYON_FONDU_AERO) {
          h = lerp(this.hAerodrome, h, smoothstep(RAYON_PLAT_AERO, RAYON_FONDU_AERO, dAero))
        }

        const i = iz * N + ix
        this.hauteurs[i] = h

        let meilleur = 0
        let meilleurPoids = -1
        for (let r = 0; r < NB_REGIONS; r++) {
          const w = p[r] ?? 0
          if (w > meilleurPoids) {
            meilleurPoids = w
            meilleur = r
          }
        }
        this.regionsGrille[i] = meilleur
      }
    }
  }

  private cell(i: number): number {
    return this.hauteurs[i] ?? SEA_LEVEL
  }

  // ---------------------------------------------------------------- lecture

  /** Vrai si le point est dans l'emprise carree de l'ile. */
  contient(x: number, z: number): boolean {
    return x >= this.x0 && z >= this.z0 && x <= this.x0 + this.cote && z <= this.z0 + this.cote
  }

  hauteur(x: number, z: number): number {
    const gx = (x - this.x0) / TERRAIN_CELL
    const gz = (z - this.z0) / TERRAIN_CELL
    let ix = Math.floor(gx)
    let iz = Math.floor(gz)
    if (ix < 0) ix = 0
    if (iz < 0) iz = 0
    if (ix > N - 2) ix = N - 2
    if (iz > N - 2) iz = N - 2
    const fx = clamp(gx - ix, 0, 1)
    const fz = clamp(gz - iz, 0, 1)
    const i = iz * N + ix
    const a = this.cell(i)
    const b = this.cell(i + 1)
    const c = this.cell(i + N)
    const d = this.cell(i + N + 1)
    return lerp(lerp(a, b, fx), lerp(c, d, fx), fz)
  }

  /** Les onze regions, dans l'ordre du contrat d'index. */
  regions(): readonly RegionDef[] {
    return this.defs
  }

  /** La region dominante sous ce point, ou null hors de l'emprise. */
  regionAuPoint(x: number, z: number): RegionDef | null {
    if (!this.contient(x, z)) return null
    const ix = clamp(Math.round((x - this.x0) / TERRAIN_CELL), 0, N - 1)
    const iz = clamp(Math.round((z - this.z0) / TERRAIN_CELL), 0, N - 1)
    const id = ORDRE_REGIONS[this.regionsGrille[iz * N + ix] ?? 0]
    return id ? REGIONS[id] : null
  }

  biome(x: number, z: number): BiomeId {
    return this.regionAuPoint(x, z)?.biome ?? 'plage'
  }

  pente(x: number, z: number): number {
    const d = TERRAIN_CELL
    const gx = (this.hauteur(x + d, z) - this.hauteur(x - d, z)) / (2 * d)
    const gz = (this.hauteur(x, z + d) - this.hauteur(x, z - d)) / (2 * d)
    return Math.hypot(gx, gz)
  }

  estEau(x: number, z: number): boolean {
    return this.hauteur(x, z) < SEA_LEVEL
  }

  echantillon(x: number, z: number): TerrainSample {
    const hauteur = this.hauteur(x, z)
    const region = this.regionAuPoint(x, z)
    const sol: SolId = hauteur < SEA_LEVEL ? 'eau' : (region?.sol ?? 'sable')
    return {
      hauteur,
      biome: region?.biome ?? 'plage',
      pente: this.pente(x, z),
      sol,
      // C'est le LIEU qui parle en premier; sans lui, ce serait le biome — et
      // onze regions n'ont que huit biomes entre elles. Le pas partage, lui,
      // passe par `vitesseDuLieu`: il lit cette valeur a chaque tick et n'a pas
      // besoin qu'on lui calcule une pente pour ca.
      vitesse: region?.vitesse,
      brume: region?.couleurBrume,
    }
  }

  normale(x: number, z: number, sortie: [number, number, number]): void {
    const d = TERRAIN_CELL
    const gx = (this.hauteur(x + d, z) - this.hauteur(x - d, z)) / (2 * d)
    const gz = (this.hauteur(x, z + d) - this.hauteur(x, z - d)) / (2 * d)
    const inv = 1 / Math.sqrt(gx * gx + gz * gz + 1)
    sortie[0] = -gx * inv
    sortie[1] = inv
    sortie[2] = -gz * inv
  }

  /**
   * Hauteur minimale et maximale sur un rectangle du monde.
   *
   * C'est un MINIMUM EXACT sur la grille au metre, pas un test des quatre
   * coins: la lecon de la piste d'aeroport, ou « un sommet ne dit la hauteur du
   * sol qu'EN UN POINT ». Le rendu de l'eau s'en sert pour savoir si une maille
   * est entierement seche — un ruisseau de trois metres au milieu d'une maille
   * de quatre ferait sinon un trou dans la mer.
   */
  bornes(x0: number, x1: number, z0: number, z1: number): { min: number; max: number } {
    const ia = clamp(Math.floor((x0 - this.x0) / TERRAIN_CELL), 0, N - 1)
    const ib = clamp(Math.ceil((x1 - this.x0) / TERRAIN_CELL), 0, N - 1)
    const ja = clamp(Math.floor((z0 - this.z0) / TERRAIN_CELL), 0, N - 1)
    const jb = clamp(Math.ceil((z1 - this.z0) / TERRAIN_CELL), 0, N - 1)
    let min = Infinity
    let max = -Infinity
    for (let iz = ja; iz <= jb; iz++) {
      for (let ix = ia; ix <= ib; ix++) {
        const h = this.cell(iz * N + ix)
        if (h < min) min = h
        if (h > max) max = h
      }
    }
    if (min === Infinity) return { min: SEA_LEVEL, max: SEA_LEVEL }
    return { min, max }
  }

  /** Hauteur minimale et maximale d'un chunk, pour les spheres englobantes. */
  bornesChunk(cx: number, cz: number): { min: number; max: number } {
    const x = this.x0 + cx * CHUNK_SIZE
    const z = this.z0 + cz * CHUNK_SIZE
    return this.bornes(x, x + CHUNK_SIZE, z, z + CHUNK_SIZE)
  }

  /**
   * La couleur du sol, region par region.
   *
   * `couleurSol` de l'ile 1 deduit la teinte de la POSITION dans la disposition
   * de ses cinq biomes — plage a l'ouest, montagne a l'est. Appelee ici, a
   * x = 1300, elle repondrait « montagne » et peindrait une plage en roche
   * grise. Chaque ile porte donc sa propre reponse.
   *
   * Le melange se fait sur les POIDS, pas sur la region dominante: sinon la
   * frontiere entre la neige et le cristal serait un trait de crayon.
   */
  couleurSol(x: number, z: number): number {
    const p = this.tampon
    this.poidsRegions(x, z, p)
    const h = this.hauteur(x, z)
    const tache = clamp(fbm(x * 0.045, z * 0.045, this.seed + 6101, 3) * 1.8 - 0.4, 0, 1)
    const grain = fbm(x * 0.135, z * 0.135, this.seed + 6104, 2)
    const pointille = hache2(Math.floor(x * 3), Math.floor(z * 3), this.seed + 6102)

    let r = 0
    let v = 0
    let b = 0
    for (let i = 0; i < NB_REGIONS; i++) {
      const w = p[i] ?? 0
      if (w <= POIDS_MIN) continue
      const def = this.defs[i] as RegionDef
      const c = melangerCouleur(def.couleurSol, def.couleurSolVariante, tache)
      r += w * ((c >> 16) & 255)
      v += w * ((c >> 8) & 255)
      b += w * (c & 255)
    }
    let couleur = (Math.round(clamp(r, 0, 255)) << 16) | (Math.round(clamp(v, 0, 255)) << 8) | Math.round(clamp(b, 0, 255))

    // Le bas de plage est mouille partout, quelle que soit la region: c'est la
    // mer qui le decide, pas le pays.
    const mouille = smoothstep(2.4, 0.05, h)
    couleur = melangerCouleur(couleur, LAISSE_MER, mouille * smoothstep(0.42, 0.9, grain))
    couleur = melangerCouleur(couleur, SABLE_MOUILLE, mouille * mouille * 0.85)
    // Un mouchete par sommet: sans lui, le sol est un aplat de peinture.
    couleur = melangerCouleur(couleur, pointille > 0.5 ? 0xffffff : 0x000000, Math.abs(pointille - 0.5) * 0.16)
    return couleur
  }
}

// ------------------------------------------------------------------ couleurs

const SABLE_MOUILLE = 0xc9a86f
const LAISSE_MER = 0xe0c893

// -------------------------------------------------------------------- relief

/**
 * LA RECETTE DE RELIEF, la meme pour les onze regions.
 *
 * Onze fonctions ecrites a la main seraient onze endroits ou se tromper sur
 * `MAX_WALK_SLOPE`, et onze choses a reprendre le jour ou l'on veut « un canyon
 * plus profond ». Il n'y en a qu'une, et une gorge n'est qu'une crete
 * d'amplitude NEGATIVE.
 *
 * `sel` est l'indice de la region: deux regions qui partagent la meme recette
 * ne partagent pas le meme bruit, sinon elles auraient les memes bosses aux
 * memes endroits.
 */
export function hauteurRegion(r: ReliefDef, x: number, z: number, seed: number, sel: number): number {
  const houle = (fbm(x / r.houle.onde, z / r.houle.onde, seed + 500 + sel, 3) - 0.5) * r.houle.ampli
  const replat = smoothstep(0.36, 0.7, fbm(x * 0.011, z * 0.011, seed + 520 + sel, 2)) * r.replat

  // Domaine tourne ET deforme: sans ca, les plis se rangent sur les axes du
  // monde et l'on voit la grille a travers la montagne.
  const wx = x + (fbm(x * 0.0055, z * 0.0055, seed + 540 + sel, 2) - 0.5) * 120
  const wz = z + (fbm(x * 0.0055, z * 0.0055, seed + 541 + sel, 2) - 0.5) * 120
  const ax = wx / r.crete.onde
  const az = wz / r.crete.onde
  const co = Math.cos(r.crete.tourne)
  const si = Math.sin(r.crete.tourne)
  const crete = (fbmCrete(ax * co - az * si, ax * si + az * co, seed + 560 + sel, 3) - 0.44) * r.crete.ampli

  let h = r.socle + houle * (1 - 0.85 * replat) + crete * (1 - 0.9 * replat)

  if (r.dune) {
    const u = x * Math.cos(r.dune.cap) + z * Math.sin(r.dune.cap)
    h += Math.sin(u / r.dune.onde) * r.dune.ampli * (1 - 0.72 * replat)
  }

  if (r.marche > 0) {
    // Des gradins, mais montables: on quantifie, puis on adoucit la marche.
    const q = Math.floor(h / r.marche)
    const f = h / r.marche - q
    h = (q + smoothstep(0.2, 0.8, f)) * r.marche
  }

  return h + (fbm(x * 0.105, z * 0.105, seed + 580 + sel, 2) - 0.5) * r.grain
}

/**
 * Le recul du trait de cote le long d'un bord, en metres.
 *
 * C'est ce qui donne a l'ile ses baies et ses caps au lieu d'un carre franc.
 * Deux echelles: de grandes anses, et un decoupage plus fin par-dessus. Le
 * recul reste POSITIF — la cote ne sort jamais du carre, sinon le terrain de
 * l'ile deborderait de l'emprise que `contient` declare.
 */
function criqueCote(u: number, graine: number): number {
  const grand = fbm(u * 0.0075, 0, graine, 3)
  const fin = fbm(u * 0.031, 0, graine + 7, 2)
  return RECUL_COTE + (grand - 0.5) * AMPLITUDE_CRIQUE + (fin - 0.5) * (AMPLITUDE_CRIQUE * 0.45)
}

/** Distance d'un point au segment [a, b]. */
function distanceSegment(x: number, z: number, ax: number, az: number, bx: number, bz: number): number {
  const vx = bx - ax
  const vz = bz - az
  const len2 = vx * vx + vz * vz
  if (len2 < 1e-6) return Math.hypot(x - ax, z - az)
  const t = clamp(((x - ax) * vx + (z - az) * vz) / len2, 0, 1)
  return Math.hypot(x - (ax + vx * t), z - (az + vz * t))
}
